import type { ResultsWaitingEntry } from "./types";
import { createAdmission, orientAdmission } from "@/core/admissions/admission-store";
import { readJson, writeJson, getStorageItem, setStorageItem } from "@/core/tenant/hospitol-storage";

const KEY = "hospitol.resultsWaiting";

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = getStorageItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(list: ResultsWaitingEntry[]): void {
  setStorageItem(KEY, JSON.stringify(list));
  window.dispatchEvent(new CustomEvent("hospitol:results-waiting"));
}

function normalize(raw: Record<string, unknown>): ResultsWaitingEntry {
  return {
    id: String(raw.id ?? ""),
    patientId: String(raw.patientId ?? ""),
    labOrderId: String(raw.labOrderId ?? ""),
    orderNumber: String(raw.orderNumber ?? ""),
    testName: String(raw.testName ?? ""),
    sampleDate: raw.sampleDate ? String(raw.sampleDate) : null,
    resultReadyAt: raw.resultReadyAt ? String(raw.resultReadyAt) : null,
    notifiedAt: raw.notifiedAt ? String(raw.notifiedAt) : null,
    status: (raw.status as ResultsWaitingEntry["status"]) ?? "awaiting_analysis",
    createdAt: String(raw.createdAt ?? new Date().toISOString()),
  };
}

function readEntries(): ResultsWaitingEntry[] {
  return readJson<Record<string, unknown>[]>(KEY, []).map(normalize);
}

export function registerLabOrderForResultsWaiting(input: {
  patientId: string;
  labOrderId: string;
  orderNumber: string;
  testName: string;
}): ResultsWaitingEntry {
  const list = readEntries();
  const existing = list.find((e) => e.labOrderId === input.labOrderId);
  if (existing) return existing;

  const entry: ResultsWaitingEntry = {
    id: `rw-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    patientId: input.patientId,
    labOrderId: input.labOrderId,
    orderNumber: input.orderNumber,
    testName: input.testName,
    sampleDate: null,
    resultReadyAt: null,
    notifiedAt: null,
    status: "awaiting_analysis",
    createdAt: new Date().toISOString(),
  };
  list.unshift(entry);
  writeJson(list);
  return entry;
}

export function markLabResultReady(labOrderId: string): ResultsWaitingEntry | null {
  const list = readEntries();
  const index = list.findIndex((e) => e.labOrderId === labOrderId);
  if (index < 0) return null;
  const now = new Date().toISOString();
  list[index] = {
    ...list[index]!,
    status: "ready",
    resultReadyAt: now,
    notifiedAt: now,
  };
  writeJson(list);
  return list[index]!;
}

export function listResultsWaiting(filter?: {
  status?: ResultsWaitingEntry["status"] | ResultsWaitingEntry["status"][];
  patientId?: string;
}): ResultsWaitingEntry[] {
  let list = readEntries();
  if (filter?.patientId) list = list.filter((e) => e.patientId === filter.patientId);
  if (filter?.status) {
    const statuses = Array.isArray(filter.status) ? filter.status : [filter.status];
    list = list.filter((e) => statuses.includes(e.status));
  }
  return list.sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
}

export function markResultsCollected(id: string): ResultsWaitingEntry | null {
  const list = readEntries();
  const index = list.findIndex((e) => e.id === id);
  if (index < 0) return null;
  list[index] = { ...list[index]!, status: "collected" };
  writeJson(list);
  return list[index]!;
}

export function countResultsReady(): number {
  return listResultsWaiting({ status: "ready" }).length;
}

/** Accueil — enregistre le retour patient et oriente vers consultation résultats. */
export function admitPatientForLabResults(entryId: string): {
  admissionId: string;
  patientId: string;
} | null {
  const entry = readEntries().find((e) => e.id === entryId);
  if (!entry || entry.status !== "ready") return null;

  const admission = createAdmission({
    patientId: entry.patientId,
    channel: "admission",
    motif: `Retour résultats — ${entry.testName} (${entry.orderNumber})`,
  });

  orientAdmission(admission.id, ["Consultation résultats"]);
  markResultsCollected(entryId);
  return { admissionId: admission.id, patientId: entry.patientId };
}
